import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faGear, faImage, faTrash } from "@fortawesome/free-solid-svg-icons";
import { deleteTheme, fetchAllThemes, selectTheme, updateThemeMedia, uploadNewTheme } from "../adminApi";
import { confirmDelete, notifyError, notifySuccess } from "../adminUtils";
import { ImagePicker, Modal } from "../components";

// order matches the PHP form: thumbnail, logo, desktop background, mobile background
const IMAGE_FIELDS = [
  { field: "thumbnail", label: "Upload Thumbnail", width: 200, height: 300, current: "themeImage" },
  { field: "logo", label: "Upload Logo", width: 400, height: 300, current: "logo" },
  { field: "desktopBg", label: "Upload Desktop Background", width: 1920, height: 1080, current: "background_desk" },
  { field: "mobileBg", label: "Upload Mobile Background", width: 414, height: 896, current: "background_mob" },
];
const EXTS = ["jpg", "jpeg", "png"];

function ThemeForm({ theme, onClose, onSaved }) {
  const editing = Boolean(theme);
  const [name, setName] = useState(theme?.newthemeName || "");
  const [description, setDescription] = useState(theme?.themeDescription || "");
  const [files, setFiles] = useState({});
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    if (!trimmedName || !trimmedDescription) {
      return notifyError("Theme Name and Theme Details cannot be empty or spaces only");
    }
    if (!editing && IMAGE_FIELDS.some(({ field }) => !files[field])) {
      return notifyError("All files are required");
    }

    setSaving(true);
    try {
      const args = { name: trimmedName, description: trimmedDescription, files };
      if (editing) await updateThemeMedia({ ...args, themeName: theme.themename });
      else await uploadNewTheme(args);
      await notifySuccess("Successfully Updated");
      onSaved();
    } catch (err) {
      notifyError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="dc-field">
        <label className="dc-label">Theme Name</label>
        <input
          className="dc-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter theme name"
          maxLength={100}
        />
        <p className="dc-format-note">*max 100 character allowed</p>
      </div>
      <div className="dc-field">
        <label className="dc-label">Theme Description</label>
        <input
          className="dc-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter theme Description"
          maxLength={200}
        />
        <p className="dc-format-note">*max 200 character allowed</p>
      </div>
      <label className="dc-label">Upload Background and Thumbnail</label>
      <p className="dc-format-note">* Allowed image formats are JPG, JPEG, PNG</p>
      <div className="dc-picker-grid">
        {IMAGE_FIELDS.map(({ field, label, width, height, current }) => (
          <ImagePicker
            key={field}
            label={label}
            spec={{ exts: EXTS, width, height }}
            currentUrl={editing ? theme[current] : ""}
            onChange={(file) => setFiles((prev) => ({ ...prev, [field]: file }))}
          />
        ))}
      </div>
      <div className="dc-modal-actions">
        <button type="button" className="dc-btn dc-btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="dc-btn dc-btn-outline" disabled={saving}>
          {saving ? "Saving..." : editing ? "Update" : "Save"}
        </button>
      </div>
    </form>
  );
}

export default function Themes() {
  const navigate = useNavigate();
  const [themes, setThemes] = useState(null);
  const [modal, setModal] = useState(null); // null | "create" | theme row being edited

  const load = useCallback(async () => {
    try {
      setThemes(await fetchAllThemes());
    } catch (err) {
      setThemes([]);
      notifyError(err.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const choose = async (theme) => {
    try {
      await selectTheme(theme.themename);
      await notifySuccess("Theme Selected");
      load();
    } catch (err) {
      notifyError(err.message);
    }
  };

  const remove = async (e, theme) => {
    e.stopPropagation();
    if (!(await confirmDelete("Are you sure you want to delete theme"))) return;
    try {
      await deleteTheme(theme.themename);
      load();
    } catch (err) {
      notifyError(err.message);
    }
  };

  const saved = () => {
    setModal(null);
    load();
  };

  const selected = (themes || []).find((t) => String(t.selected) === "true");

  return (
    <div className="dc-page">
      <div className="dc-themes-layout">
        <div className="dc-selected">
          {selected && (
            <>
              <img src={selected.themeImage} alt="" className="dc-selected-img" />
              <div className="dc-selected-text">
                <h4>{selected.newthemeName}</h4>
                <h5>{selected.themeDescription}</h5>
              </div>
            </>
          )}
        </div>

        <div className="dc-card">
          <div className="dc-card-head">
            <h5 className="dc-customtitle">Select Themes</h5>
            <button className="dc-btn dc-btn-outline" onClick={() => setModal("create")}>
              + Create Theme
            </button>
          </div>
          {themes === null ? (
            <p>Loading...</p>
          ) : (
            <div className="dc-theme-grid">
              {themes.map((theme) => (
                <div key={theme.themename} className="dc-theme-card" onClick={() => choose(theme)}>
                  <img src={theme.themeImage} alt={theme.newthemeName} className="dc-theme-image" />
                  {String(theme.selected) === "true" && (
                    <div className="dc-theme-select">
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                  )}
                  <div className="dc-icon-container">
                    <div className="dc-round-icon dc-delete" title="Delete" onClick={(e) => remove(e, theme)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </div>
                    <div
                      className="dc-round-icon dc-photo"
                      title="Edit theme"
                      onClick={(e) => {
                        e.stopPropagation();
                        setModal(theme);
                      }}
                    >
                      <FontAwesomeIcon icon={faImage} />
                    </div>
                    <div
                      className="dc-round-icon dc-gear"
                      title="Settings"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/rules/${theme.themename}`);
                      }}
                    >
                      <FontAwesomeIcon icon={faGear} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal open={Boolean(modal)} onClose={() => setModal(null)}>
        {modal && <ThemeForm key={modal === "create" ? "create" : modal.themename} theme={modal === "create" ? null : modal} onClose={() => setModal(null)} onSaved={saved} />}
      </Modal>
    </div>
  );
}
